import { useId, type ComponentProps } from "react";
import { BellIcon } from "lucide-react";
import { Button, Badge, Switch, Spinner, cn } from "@sfc/ui";
import { type InboxItem, type PreferenceItem } from "./contracts";

export function NotificationBadge({
  count,
  unavailable = false,
  className,
  ...buttonProps
}: {
  count: number | undefined;
  unavailable?: boolean;
} & Omit<ComponentProps<typeof Button>, "children">) {
  const label = unavailable
    ? "Notifications, count unavailable"
    : count === undefined
      ? "Notifications"
      : `Notifications, ${count} unread`;
  return (
    <Button
      {...buttonProps}
      type="button"
      variant="nav"
      size="icon"
      className={cn("relative min-h-11 min-w-11", className)}
      aria-label={label}
    >
      <BellIcon className="size-5" />
      {count !== undefined && count > 0 && (
        <Badge className="absolute -top-1 -right-1" aria-hidden="true">
          {count > 99 ? "99+" : count}
        </Badge>
      )}
      {unavailable && (
        <span
          className="absolute top-1 right-1 size-2 rounded-full bg-warning"
          aria-hidden="true"
        />
      )}
    </Button>
  );
}

export function NotificationInbox({
  items,
  pending,
  onStateChange,
  onOpen,
  hasMore,
  onLoadMore,
  loadingMore = false,
}: {
  items: InboxItem[];
  pending: (id: string) => boolean;
  onStateChange: (
    id: string,
    state: { read?: boolean; archived?: boolean },
  ) => void;
  onOpen: (item: InboxItem) => void;
  hasMore: boolean;
  onLoadMore: () => void;
  loadingMore?: boolean;
}) {
  return (
    <div className="flex min-w-0 flex-col gap-4">
      {items.length === 0 ? (
        <p className="body-md py-8 text-center text-text-muted">
          No notifications in this view.
        </p>
      ) : (
        <ul className="flex min-w-0 flex-col divide-y divide-border">
          {items.map((item) => (
            <li key={item.id} className="flex min-w-0 flex-col gap-3 py-4">
              <div className="flex items-start justify-between gap-3">
                <h3
                  className={cn(
                    "body-md min-w-0 break-words",
                    item.readAt === null && "font-semibold",
                  )}
                >
                  {item.title}
                </h3>
                {item.readAt === null && <Badge variant="base">Unread</Badge>}
              </div>
              <p className="body-md whitespace-pre-wrap break-words text-text-secondary">
                {item.body}
              </p>
              <time
                className="body-sm text-text-muted"
                dateTime={item.createdAt}
              >
                {new Date(item.createdAt).toLocaleString()}
              </time>
              <div className="flex flex-wrap gap-2">
                {item.href && (
                  <Button
                    variant="outline"
                    className="min-h-11"
                    onClick={() => onOpen(item)}
                    disabled={pending(item.id)}
                  >
                    Open
                  </Button>
                )}
                <Button
                  variant="ghost"
                  className="min-h-11"
                  disabled={pending(item.id)}
                  onClick={() =>
                    onStateChange(item.id, { read: item.readAt === null })
                  }
                >
                  {item.readAt === null ? "Mark read" : "Mark unread"}
                </Button>
                <Button
                  variant="ghost"
                  className="min-h-11"
                  disabled={pending(item.id)}
                  onClick={() =>
                    onStateChange(item.id, {
                      archived: item.archivedAt === null,
                    })
                  }
                >
                  {item.archivedAt === null ? "Archive" : "Restore"}
                </Button>
                {pending(item.id) && (
                  <Spinner aria-label="Updating notification" />
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
      {hasMore && (
        <Button variant="outline" disabled={loadingMore} onClick={onLoadMore}>
          {loadingMore ? "Loading more" : "Next page"}
        </Button>
      )}
    </div>
  );
}

export function NotificationPreferences({
  items,
  pending,
  onChange,
}: {
  items: PreferenceItem[];
  pending: (type: string) => boolean;
  onChange: (
    type: string,
    channel: "inApp" | "email",
    enabled: boolean,
  ) => void;
}) {
  const controlId = useId();
  return (
    <div className="flex flex-col gap-5">
      <p className="body-sm text-text-secondary">
        Choose how to receive each notification. Changes apply to future
        notifications; turning off email also stops emails that have not started
        sending.
      </p>
      {items.map((item) => (
        <fieldset
          key={item.type}
          disabled={pending(item.type)}
          className="flex flex-col gap-3 rounded-lg border border-border p-4"
        >
          <legend className="body-md px-1 font-medium">{item.label}</legend>
          <p className="body-sm text-text-secondary">{item.description}</p>
          <div className="flex flex-wrap gap-6">
            {item.channels.map((channel) => (
              <div key={channel} className="flex min-h-11 items-center gap-3">
                <Switch
                  id={`${controlId}-${item.type}-${channel}`}
                  disabled={pending(item.type)}
                  checked={item.values[channel]}
                  onCheckedChange={(enabled) =>
                    onChange(item.type, channel, enabled)
                  }
                  aria-label={`${item.label}: ${channel === "inApp" ? "In-app" : "Email"}`}
                />
                <label
                  htmlFor={`${controlId}-${item.type}-${channel}`}
                  className="body-md"
                >
                  {channel === "inApp" ? "In-app" : "Email"}
                </label>
              </div>
            ))}
          </div>
          {pending(item.type) && (
            <Spinner aria-label="Saving notification preferences" />
          )}
        </fieldset>
      ))}
    </div>
  );
}
